import { leavesNoAdmin, type Member, type Role } from '@rp/contracts'
import { Button, Dialog, Segmented } from '../../design'
import { SESSION_COPY } from '../../session/copy'
import { SETTINGS_COPY } from './copy'
import { abilitiesLost, isDemotion, ROLE_OPTIONS } from './roleOptions'

export type RoleDraft = { member: Member; role: Role }

type MemberRoleDialogProps = {
  draft: RoleDraft | null
  members: readonly Member[]
  selfId: string
  pending: boolean
  onRoleChange: (role: Role) => void
  onCancel: () => void
  onConfirm: (draft: RoleDraft) => void
}

const nameOf = (member: Member): string => member.displayName ?? member.email

const DemotionWarning = ({ draft, isSelf }: { draft: RoleDraft; isSelf: boolean }) => (
  <>
    <p className="measure">
      {SETTINGS_COPY.demoteBody(
        nameOf(draft.member),
        SESSION_COPY.roleLabels[draft.member.role],
        SESSION_COPY.roleLabels[draft.role],
        abilitiesLost(draft.member.role, draft.role),
      )}
    </p>
    {isSelf ? <p className="measure text-tone tone-danger">{SETTINGS_COPY.selfDemoteNote}</p> : null}
  </>
)

const assess = (draft: RoleDraft | null, members: readonly Member[]) => ({
  demoting: draft !== null && isDemotion(draft.member.role, draft.role),
  blocked: draft !== null && leavesNoAdmin(members, { userId: draft.member.userId, role: draft.role }),
  unchanged: draft === null || draft.role === draft.member.role,
})

export const MemberRoleDialog = (props: MemberRoleDialogProps) => {
  const { draft, members, selfId, pending, onRoleChange, onCancel, onConfirm } = props
  const { demoting, blocked, unchanged } = assess(draft, members)

  const footer = (
    <>
      <Button onClick={onCancel}>{SETTINGS_COPY.cancel}</Button>
      {blocked || draft === null ? null : (
        <Button
          variant={demoting ? 'danger-solid' : 'primary'}
          disabled={unchanged}
          pending={pending}
          pendingLabel={SETTINGS_COPY.changingRole}
          onClick={() => onConfirm(draft)}
        >
          {demoting ? SETTINGS_COPY.demoteConfirm : SETTINGS_COPY.changeRoleConfirm}
        </Button>
      )}
    </>
  )

  return (
    <Dialog
      open={draft !== null}
      title={SETTINGS_COPY.changeRoleTitle}
      onClose={onCancel}
      tone={demoting ? 'danger' : 'neutral'}
      edge={demoting ? 'hatch' : 'plain'}
      footer={footer}
    >
      {draft === null ? null : (
        <>
          <Segmented
            label={SETTINGS_COPY.changeRoleLegend(nameOf(draft.member))}
            name="member-role"
            value={draft.role}
            options={ROLE_OPTIONS}
            onChange={onRoleChange}
          />
          {blocked ? <p className="measure text-tone tone-danger">{SETTINGS_COPY.lastAdminHint}</p> : null}
          {demoting && !blocked ? <DemotionWarning draft={draft} isSelf={draft.member.userId === selfId} /> : null}
        </>
      )}
    </Dialog>
  )
}
